// Types and pure helpers for the locality curation console.
//
// Kept out of the page module on purpose: exporting non-component values alongside a
// component breaks React Fast Refresh ("export is incompatible"), which Vite reported on
// every edit of the page while these lived there.

export type LocalityKind = "country" | "city";

export type LocalityFrame = {
  id: string;
  viewKind: string | null;
  sourceProvider: string;
  lifecycleState: string;
  displayRef: string;
  thumbnailRef: string | null;
  license: string;
  attribution: string | null;
};

export type AdminLocality = {
  id: string;
  kind: LocalityKind;
  name: string;
  countryIsoCode: string | null;
  frames: LocalityFrame[];
};

/** Carousel order on the wire: banner → main → night → panorama. */
const VIEW_KIND_ORDER = ["banner", "main", "night", "panorama"];

/**
 * The frames a traveller actually sees, in the order they appear.
 *
 * Mirrors the server-side selection policy: any curated frame makes the whole locality
 * curated, and harvested frames drop out entirely — per-slot precedence was not enough,
 * because a leftover harvested `banner` would otherwise hold the first position while the
 * curated `main` sat behind it.
 */
export function activeFrames(frames: readonly LocalityFrame[]): LocalityFrame[] {
  const active = frames.filter((frame) => frame.lifecycleState === "active");
  const curated = active.filter((frame) => frame.sourceProvider === "curated");
  const winning = curated.length > 0 ? curated : active;
  return [...winning].sort(
    (left, right) =>
      VIEW_KIND_ORDER.indexOf(left.viewKind ?? "") - VIEW_KIND_ORDER.indexOf(right.viewKind ?? ""),
  );
}

export function isCurated(locality: AdminLocality): boolean {
  return locality.frames.some(
    (frame) => frame.sourceProvider === "curated" && frame.lifecycleState === "active",
  );
}
